/**
 * The loop: one frame at a time, it draws the intro until the globe has set,
 * hands the globe over to the page's camera, and from then on flies the camera
 * on the scroll. Also here: jumping to a section.
 *
 * It owns the current viewport and passes it on to everything it calls.
 */
import { debug } from './debug'
import { clamp, smootherstep, smoothstep } from './easing'
import { createCamera, mixView } from './camera'
import type { Globe } from './globe'
import { INTRO_DURATION, type Intro } from './intro/intro'
import type { Cue } from './page/cue'
import type { Outline } from './page/outline'
import type { Pins } from './page/pins'
import type { PlaceList } from './page/place-list'
import type { Stop } from './page/stops'
import type { Renderer } from './renderer/renderer'
import { frameOf, isPhone, type Viewport } from './viewport'

const RAD = Math.PI / 180

/* =====================================================================
   TWEAKABLES. Times are seconds.
   ===================================================================== */
const SPEED = 1.0 /* the whole animation: 1 = as designed, 1.3 = 30 % faster */
const HANDOFF = 1.8 /* the globe glides from the intro's centre to the hero, growing as it goes */
const SCROLL_K = 5.5 /* how fast the camera catches up with the scroll (higher = tighter) */
const PARALLAX = 3.5 /* deg the mouse tilts the globe (0 = off) */
/* ===================================================================== */

export interface LoopParts {
  readonly renderer: Renderer
  readonly intro: Intro
  readonly camera: ReturnType<typeof createCamera>
  readonly globe: Globe
  readonly pins: Pins
  readonly outline: Outline
  readonly placeList: PlaceList
  readonly cue: Cue
  /** Every stop, and the places among them (the stops that are not the global one). */
  readonly stops: Stop[]
  readonly places: Stop[]
  readonly viewport: Viewport
  /** The reader prefers reduced motion: no intro, and no tilt with the mouse. */
  readonly reduced: boolean
  /**
   * The section a same-site arrival carried in `?from=`, to open settled on; null
   * on an arrival from outside, which plays the intro.
   */
  readonly carried: HTMLElement | null
}

export interface Loop {
  /** One frame, at the time `now` (ms) the browser gives. */
  frame(now: number): void
  /** The current viewport, for whatever needs to size itself to the frame. */
  readonly viewport: Viewport
  /** Starts the intro, or hands over at once when it is skipped. */
  start(): void
  /** Scrolls to a section, flying the camera there. */
  goTo(el: HTMLElement): void
  /** The window changed size. */
  resized(viewport: Viewport): void
  /** The webfonts have loaded, so the type measures differently. */
  fontsLoaded(): void
  /** The mouse moved to (x, y) in the window. */
  pointerAt(x: number, y: number): void
}

function byId(id: string): HTMLElement {
  return document.getElementById(id)!
}

export function createLoop(parts: LoopParts): Loop {
  const { renderer, intro, camera, globe, pins, outline, placeList, cue, stops, places, carried } =
    parts
  const ctx = renderer.ctx
  const topBar = byId('top')
  const hero = byId('about')
  const main = byId('main')
  const contact = byId('contact')
  const parallax = parts.reduced ? 0 : PARALLAX
  /* testing: a fixed scroll position, in screen heights */
  const fixedScroll = debug.scroll
  let viewport = parts.viewport
  let introOn = true
  let skipped = false
  let started = 0
  let lastNow = 0
  /* when the intro ended; the smoothed scroll; the time of the last frame */
  let tEnd = 0
  let smoothed = 0
  let tNow = 0
  /* the mouse, smoothed (mx, my) and where it is (mxT, myT), each -1..1 across the window */
  let mx = 0
  let my = 0
  let mxT = 0
  let myT = 0
  let scrolledOnce = false

  return {
    frame,
    get viewport() {
      return viewport
    },
    start() {
      if (
        parts.reduced ||
        debug.skip ||
        fixedScroll !== null ||
        carried ||
        (navigator.webdriver && !debug.intro)
      ) {
        skipped = true
        const settle = INTRO_DURATION + (fixedScroll !== null ? HANDOFF + 0.5 : 0)
        started = performance.now() - (settle * 1000) / SPEED
        introOn = true
      } else {
        startIntro()
        if (debug.time !== null) started -= (debug.time * 1000) / SPEED
      }
    },
    goTo,
    resized(next) {
      viewport = next
      intro.measureWordmark()
      if (!introOn) camera.measureKeys(viewport)
    },
    fontsLoaded() {
      intro.measureWordmark()
      if (introOn) {
        intro.measureArms()
        intro.reseed(isPhone())
      }
      if (!introOn) camera.measureKeys(viewport)
    },
    pointerAt(x, y) {
      mxT = (x / viewport.width) * 2 - 1
      myT = (y / viewport.height) * 2 - 1
    },
  }

  function frame(now: number) {
    const dt = lastNow ? Math.min((now - lastNow) / 1000, 0.1) : 1 / 60
    lastNow = now
    const t = ((now - started) / 1000) * SPEED

    if (introOn) {
      if (t < INTRO_DURATION) {
        intro.render(t, viewport)
        return
      }
      endIntro(t)
    }

    /* the scroll, smoothed */
    const vp = viewport
    const H = vp.height
    const sy = fixedScroll !== null ? fixedScroll * H : window.pageYOffset
    if (fixedScroll !== null) {
      main.style.transform = 'translateY(' + -sy + 'px)'
      contact.style.transform = main.style.transform
    }
    const sT = sy / Math.max(H, 1)
    if (!camera.flying) smoothed += (sT - smoothed) * (1 - Math.exp(-dt * SCROLL_K))
    if (fixedScroll !== null) smoothed = sT
    topBar.classList.toggle('stuck', sy > 40)
    if (sy > 40 || camera.flying) scrolledOnce = true
    outline.mark(vp)

    /* copy comes in as its section reaches the middle */
    for (const stop of stops) {
      const r = stop.el.getBoundingClientRect()
      stop.el.classList.toggle('on', r.top < H * 0.62 && r.bottom > H * 0.38)
    }

    camera.hoverTurn(t, dt, placeList.hovered)
    tNow = t
    const fr = camera.frameView(vp, t, smoothed, sT)
    let v = fr.v
    let gone = fr.gone
    if (fr.flightU !== null) {
      if (fr.jumpTo !== null)
        window.scrollTo(0, fr.jumpTo) /* the copy is out of sight: the page moves under it */
      if (fr.flightU > 0.62) document.body.classList.remove('flying')
      smoothed = fr.s!
    }
    /* the hand-over from the intro */
    const ho = smootherstep(0, HANDOFF, t - tEnd)
    if (ho < 1) v = mixView(camera.introView(vp, t), v, ho)
    /* the scroll cue */
    cue.update(t - tEnd - HANDOFF, scrolledOnce)
    gone *= ho /* straight after the intro the globe sinks away, not cut */
    /* the light, eased over to the side away from the copy */
    globe.follow(dt, camera.weights.away, camera.weights.shade, ho)
    const light = globe.light
    /* the mouse tilts the world a little */
    mx += (mxT - mx) * (1 - Math.exp(-dt * 3))
    my += (myT - my) * (1 - Math.exp(-dt * 3))
    v.lam -= mx * parallax * RAD
    v.tilt += my * parallax * 0.6 * RAD

    ctx.setTransform(vp.dpr, 0, 0, vp.dpr, 0, 0)
    ctx.globalCompositeOperation = 'source-over'
    ctx.globalAlpha = 1
    renderer.begin(frameOf(vp, light))
    if (gone < 0.995) {
      globe.draw(vp, v, t, camera.weights.net)
      placeList.draw(
        ctx,
        v,
        camera.weights.hero * smoothstep(0.5, 1.4, t - tEnd) * (1 - gone),
        dt,
        camera.flying,
        vp.mobile
      )
      /* grain */
      renderer.grain(0.35)
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
      if (gone > 0.005) {
        ctx.fillStyle = 'rgba(8,9,11,' + gone.toFixed(3) + ')'
        ctx.fillRect(0, 0, vp.width, vp.height)
      }
    }
    for (const place of places) place.act *= 1 - gone
    pins.place(vp)
    renderer.end()
  }

  function startIntro() {
    globe.reset()
    document.body.classList.add('locked')
    window.scrollTo(0, 0)
    topBar.classList.remove('on')
    hero.classList.remove('on')
    outline.show(false)
    pins.hideAll()
    started = performance.now()
    introOn = true
    smoothed = 0
  }

  function endIntro(t: number) {
    introOn = false
    tEnd = skipped ? t - HANDOFF - 1 : t /* skipped: already handed over */
    intro.hideWordmark()
    document.body.classList.remove('locked')
    /* the head probe held the page locked from the first paint with html.path-intro; the intro is over */
    document.documentElement.classList.remove('path-intro')
    if (!skipped) {
      document.body.classList.add('settling')
      setTimeout(() => document.body.classList.remove('settling'), 2200)
    }
    topBar.classList.add('on')
    hero.classList.add('on')
    outline.show(true)
    camera.measureKeys(viewport)
    /* a same-site arrival opens on its carried section: skipped already, the page
       is at its resting position before the first frame, so nothing animates in */
    if (carried) window.scrollTo(0, restY(carried))
    smoothed = fixedScroll !== null ? fixedScroll : window.pageYOffset / viewport.height
  }

  function goTo(el: HTMLElement) {
    if (introOn) return
    const y = restY(el)
    const s1 = y / viewport.height
    if (parts.reduced || fixedScroll !== null || !camera.hasView) {
      window.scrollTo(0, y)
      return
    }
    if (!camera.flyTo(viewport, y, s1, smoothed, tNow)) return
    placeList.clearHover()
    document.body.classList.add('flying')
  }

  /** The scroll position (px) that rests on a section, clamped to the page. */
  function restY(el: HTMLElement): number {
    const max = document.documentElement.scrollHeight - viewport.height
    return clamp(targetY(el), 0, max)
  }

  /** The page position (px) that puts a section where the camera rests on it. */
  function targetY(el: HTMLElement): number {
    if (el.classList.contains('stop'))
      return el.offsetTop + el.offsetHeight / 2 - viewport.height / 2
    if (el === hero) return 0
    return el.offsetTop - (el.id === 'contact' ? 0 : 40)
  }
}
